import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'

import { Seo } from '@/components/generals/seo'
import { ConnectSection } from '@/components/shared/connect-section'
import { activateOnKey } from '@/lib/a11y'

const planFeatures = [
    'Cloud Storage',
    'Unlimited Shares',
    'Privat Access',
    'Embed Links',
    'Comments',
    'Print PDF Ready',
    'Export to PNG',
    'Cloud Infrastructure'
]

const avatarTopIds = [
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff4f-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff54-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff59-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff5e-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff63-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff68-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff6d-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff72-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff77-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff7c-a53bb798'
]

const avatarBottomIds = [
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff82-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff87-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff8c-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff91-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff96-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355aff9b-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355affa0-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355affa5-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355affaa-a53bb798',
    'w-node-ce0130d9-87d7-23ce-8b64-b40e355affaf-a53bb798'
]

const Pricing: FC = (): ReactNode => {
    const [billing, setBilling] = useState<'monthly' | 'annually'>('annually')
    const [submitted, setSubmitted] = useState(false)

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setSubmitted(true)
    }

    const price = billing === 'annually' ? '24' : '29'

    return (
        <>
            <Seo
                title='Pricing'
                description='Simple, transparent pricing for Vancouver Plus. Start free, pick a plan later, cancel anytime.'
            />

            {/* ---------------- Hero ---------------- */}
            <div className='section hero-payment-section'>
                <div className='container'>
                    <div className='top-margin-xxl'>
                        <div className='center-text'>
                            <div className='hero-text-holder'>
                                <div className='animate-on-load-1st'>
                                    <div className='hero-text'><span className='gradient-text'>Our payment plan</span> is easy to understand</div>
                                </div>
                                <div className='w-layout-grid grid-8-col'>
                                    <div id='w-node-_1ee2bbf4-d768-b6d6-77c4-0c26a6fc854f-a53bb798' className='hero-paragraph'>
                                        <p className='hero-description'>Lorem ipsum dolor sit amet, consectetur adipiscing elit. At etiam tellus aliquet risus, odio vitae cras viverra elementum.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bg-holder payment-plan-page'><img src='/images/Circle-Line-Image.svg' loading='lazy' alt='' className='line-image' /></div>
            </div>

            {/* ---------------- Pricing ---------------- */}
            <div id='Pricing' className='section'>
                <div className='container'>
                    <div className='payment-holder'>
                        <div className='tab-list-in-pricing-page'>
                            <a
                                role='button'
                                tabIndex={0}
                                onClick={() => setBilling('monthly')}
                                onKeyDown={activateOnKey(() => setBilling('monthly'))}
                                className={`tab-link w-inline-block${billing === 'monthly' ? ' tab-link-active' : ''}`}
                            >
                                <div className='tab-text'>Monthly</div>
                            </a>
                            <a
                                role='button'
                                tabIndex={0}
                                onClick={() => setBilling('annually')}
                                onKeyDown={activateOnKey(() => setBilling('annually'))}
                                className={`tab-link w-inline-block${billing === 'annually' ? ' tab-link-active' : ''}`}
                            >
                                <div className='tab-text'>Annually</div>
                            </a>
                        </div>
                        <div className='price-tab-grid'>
                            <div id='w-node-_477c0013-9d53-6390-91a5-0711cb36bb21-a53bb798'>
                                <div className='elements-wrapper'>
                                    <div className='experience-card'>
                                        <div className='gray-line-holder'>
                                            <div className='pricing-box-holder'>
                                                <div className='payment-tab-holder'>
                                                    <div className='payment-padding'>
                                                        <div className='fade-in-move-on-scroll'>
                                                            <h2>Pro</h2>
                                                        </div>
                                                        <div className='fade-in-move-on-scroll'>
                                                            <div className='center-text'>
                                                                <div className='payment-number-holder'>
                                                                    <div className='price-currency-symbol'>$</div>
                                                                    <div className='payment-number-container'>
                                                                        <div className='short-price-number'>{price}</div>
                                                                        <div className='monthyl-and-yearly-next-to-price'>/mo</div>
                                                                    </div>
                                                                </div>
                                                                <div className='center-paragraph'>
                                                                    <p className='short-price-description-in-the-item'>
                                                                        {billing === 'annually' ? 'Billed annually. Two months free.' : 'Billed monthly. Cancel anytime.'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='fade-in-move-on-scroll _100width'>
                                                        <div className='top-margin-l _100width'>
                                                            <div className='payment-button-holder'>
                                                                <Link href='/contact' className='button w-button'>Start - 7 Days FREE</Link>
                                                                <a href='#More' className='gradient-link w-inline-block'>
                                                                    <div className='button-gradient' />
                                                                    <div className='button-content-holder button-holder-no-arrow'>
                                                                        <div className='link-gradient-text'>View Details</div>
                                                                    </div>
                                                                    <div className='white-background-button-color' />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='light-wrapper'>
                                                    <div className='light'>
                                                        <div className='gradient-inside' />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div id='w-node-_95e76025-80ea-0b65-914a-4340c006b339-a53bb798' className='content'>
                                <div className='pricing-item-content-holder'>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='pricing-item-title'>Try Our product for free for on month</h2>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <p className='pricing-item-summary'>No credit card is required, start for free, and pick a plan later. You can cancel anytime.</p>
                                    </div>
                                    <ul role='list' className='list-with-icons w-list-unstyled'>
                                        {planFeatures.map((feature) => (
                                            <li key={feature} className='list-item-with-icon'><div>{feature}</div></li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------------- Connect (avatars) ---------------- */}
            <ConnectSection
                topIds={avatarTopIds}
                bottomIds={avatarBottomIds}
                headingClassName='center-heading-holder'
            />

            {/* ---------------- Quick access + newsletter ---------------- */}
            <div id='Help' className='section'>
                <div className='container _4em'>
                    <div className='quick-access'>
                        <div className='quick-access-grid'>
                            <Link href='/about' className='quick-access-card-holder w-inline-block'>
                                <div className='quick-access-card'>
                                    <h2>Help</h2>
                                    <p>Get help using Modulify</p>
                                </div>
                                <div className='arrow-holder-container'>
                                    <img src='/images/Arrow-Gradient.svg' loading='lazy' alt='' className='_100width' />
                                </div>
                                <div className='grey-border' />
                            </Link>
                            <Link href='/contact' className='quick-access-card-holder w-inline-block'>
                                <div className='quick-access-card'>
                                    <h2>Contact</h2>
                                    <p>hello@modulify.ai</p>
                                </div>
                                <div className='arrow-holder-container'>
                                    <img src='/images/Arrow-Gradient.svg' loading='lazy' alt='' className='_100width' />
                                </div>
                                <div className='grey-border' />
                            </Link>
                            <div id='w-node-bcfa0579-6986-668e-09cb-5d36b8f087e7-b8f087d1' className='quick-access-card-holder less-padding'>
                                <div className='quick-access-card spacing'>
                                    <h2>Get to know us</h2>
                                    <div className='w-form'>
                                        {submitted ? (
                                            <div className='success-message-toast w-form-done' style={{ display: 'block' }}>
                                                <div className='success-message-text'>
                                                    <span className='text-span'><strong>Thank you!</strong></span>
                                                    <strong> </strong>Your submission has been received!
                                                </div>
                                            </div>
                                        ) : (
                                            <form name='email-form' onSubmit={onSubmit} className='form'>
                                                <div className='form-container'>
                                                    <div className='fake-submit-button-holder'>
                                                        <div className='button-arrow-holder button-with-text-holder'>
                                                            <input type='submit' className='submit-button submit-button-with-text w-button' value='Submit' />
                                                            <div className='button-gradient' />
                                                        </div>
                                                    </div>
                                                    <input className='email-input w-input' maxLength={256} name='Email' placeholder='Your Email' type='email' required />
                                                    <div className='form-gradient' />
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                                <div className='grey-border' />
                            </div>
                            <Link href='/features' className='quick-access-card-holder w-inline-block'>
                                <div className='quick-access-card'>
                                    <h2>Security</h2>
                                    <p>hello@modulify.ai</p>
                                </div>
                                <div className='arrow-holder-container'>
                                    <img src='/images/Arrow-Gradient.svg' loading='lazy' alt='' className='_100width' />
                                </div>
                                <div className='grey-border' />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Pricing
